import { Button, useToast } from "@themis/ui";
import React from "react";
import { useHistory, useParams } from "react-router-dom";

import { useAccount } from "@/api/queries/accounts";
import {
  useContact,
  useCreateContact,
  useUpdateContact,
} from "@/api/queries/contacts";
import { Header } from "@/components/Layout/Header";
import { PageContent } from "@/components/Layout/PageContent";
import { PageLayout } from "@/components/Layout/PageLayout";

import { ACCOUNT_ID_PARAM, CONTACT_ID_PARAM } from ".";
import {
  ContactInfoForm,
  ContactInfoSchema,
} from "../components/Contacts/ContactInfoForm";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Subtitle } from "../components/Subtitle";

function FormContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-flex tw-min-w-96 tw-flex-col tw-gap-2 tw-px-32">
      {children}
    </div>
  );
}

function ErrorContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-text-md tw-flex tw-h-72 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-text-primaryDim-300">
      {children}
    </div>
  );
}

export function ContactDetails() {
  const history = useHistory();
  const toast = useToast();

  const { accountId, contactId, workspace_id } = useParams<{
    workspace_id: string;
    [ACCOUNT_ID_PARAM]: string;
    [CONTACT_ID_PARAM]: string;
  }>();

  const {
    data: accountData,
    isLoading: isAccountLoading,
    error: accountError,
  } = useAccount(Number(workspace_id), Number(accountId));

  const {
    data: contactData,
    isLoading: isContactLoading,
    error: contactError,
  } = useContact(Number(contactId));

  const { mutateAsync: createContact } = useCreateContact({
    accountId: Number(accountId),
    onSuccess: () => {
      toast({ content: "Contact has been created!", variant: "success" });
      history.goBack();
    },
    onError: () => {
      toast({
        content: "Something went wrong. Could not create contact.",
        variant: "error",
      });
    },
  });

  const { mutateAsync: updateContact } = useUpdateContact({
    contactId: Number(contactId),
    onSuccess: () => {
      toast({ content: "Contact saved successfully!", variant: "success" });
      history.goBack();
    },
    onError: () => {
      toast({
        content: "Something went wrong. Could not save contact.",
        variant: "error",
      });
    },
  });

  const handleSubmitForm = async (values: ContactInfoSchema) => {
    if (!contactData) {
      await createContact(values);
      return;
    }

    await updateContact(values);
  };

  if (isContactLoading || isContactLoading) {
    return (
      <PageContent>
        {isContactLoading ? (
          <LoadingSpinner />
        ) : (
          <ErrorContainer>
            Could not load contact details
            <Button color="tertiary" onClick={history.goBack}>
              Go Back
            </Button>
          </ErrorContainer>
        )}
      </PageContent>
    );
  }

  return (
    <PageLayout>
      <Header
        onClickBackButton={history.goBack}
        title={
          <div className="tw-align-center tw-flex tw-gap-1">
            Accounts
            {!(isAccountLoading || accountError) && (
              <Subtitle>{accountData?.account.name}</Subtitle>
            )}
            {!(isContactLoading || contactError) && (
              <Subtitle>{contactData?.contact?.full_name}</Subtitle>
            )}
          </div>
        }
      />
      <PageContent>
        <FormContainer>
          <h2 className="tw-text-lg tw-font-semibold tw-text-neutral-500">
            Contact Info
          </h2>
          <ContactInfoForm
            defaultValues={contactData?.contact}
            onSubmit={handleSubmitForm}
          />
        </FormContainer>
      </PageContent>
    </PageLayout>
  );
}
